import { Measurement, measurementTagEnums } from '@kentik/ui-shared/query/query.types';
import startCase from 'lodash/startCase';
import { titleExactMatches } from './metricsTreeData';

/** Convert a single path segment to a display label, honoring known overrides. */
export function titleizeSegment(seg: string): string {
  return titleExactMatches[seg] || startCase(seg);
}

// e.g. '/metrics/interfaces/counters' → '/metrics'
export function getCategoryPrefix(measurementPath: string): string | null {
  const match = measurementPath.match(/^(\/[^/]+)/);
  return match ? match[1] : null;
}

// e.g. ('/metrics/interfaces/counters', '/metrics', 'NMS') → 'NMS / Interfaces / Counters'
export function formatMeasurementDisplay({
  measurement,
  prefix,
  categoryLabel
}: {
  measurement: string;
  prefix: string;
  categoryLabel: string;
}): string {
  if (measurement === prefix) {
    return categoryLabel;
  }
  const relativePath = measurement.startsWith(prefix) ? measurement.slice(prefix.length) : measurement;
  const formatted = relativePath
    .split('/')
    .filter(Boolean)
    .map((seg) => titleExactMatches[seg] || seg.charAt(0).toUpperCase() + seg.slice(1))
    .join(' / ');
  return `${categoryLabel} / ${formatted}`;
}

export interface SubCategory {
  /** Full path prefix, e.g. '/metrics/interfaces' */
  key: string;
  /** Human-readable label, e.g. 'Interfaces' */
  label: string;
  /** The parent category prefix, e.g. '/metrics' */
  parentKey: string;
  /** Number of measurements under this sub-category */
  measurementCount: number;
  /** Nested sub-categories (deeper levels) */
  children?: SubCategory[];
  /** True when this sub-category's key is itself a valid measurement path (not just an intermediate grouping) */
  isMeasurement?: boolean;
}

/**
 * Extract first-level sub-categories from measurements for a given category prefix.
 *
 * For example, given prefix='/metrics' and measurements like:
 *   /metrics/interfaces/counters, /metrics/system, /metrics/system/cpus
 * Returns: [{ key: '/metrics/interfaces', label: 'Interfaces' }, { key: '/metrics/system', label: 'System' }]
 *
 * If there are no sub-categories (e.g. the category only has a root measurement),
 * returns an empty array — signaling the UI should skip the sub-category step.
 */
function extractSubCategories(measurements: Measurement[], categoryPrefix: string): SubCategory[] {
  const subCatMap = new Map<string, { label: string; count: number }>();

  measurements.forEach((m) => {
    const path = m.measurement;
    if (
      !path ||
      !path.startsWith(categoryPrefix) ||
      (path.length > categoryPrefix.length && path[categoryPrefix.length] !== '/')
    ) {
      return;
    }

    // Get the remainder after the prefix: '/metrics/interfaces/counters' → '/interfaces/counters'
    const remainder = path.slice(categoryPrefix.length);

    // If the measurement IS the category root (e.g. '/traffic'), skip it for sub-category extraction
    if (!remainder || remainder === '/') {
      return;
    }

    // Extract first segment: '/interfaces/counters' → 'interfaces'
    const segments = remainder.split('/').filter(Boolean);
    if (segments.length === 0) {
      return;
    }

    const firstSegment = segments[0];
    const subKey = `${categoryPrefix}/${firstSegment}`;

    const existing = subCatMap.get(subKey);
    if (existing) {
      existing.count += 1;
    } else {
      const label = titleizeSegment(firstSegment);
      subCatMap.set(subKey, { label, count: 1 });
    }
  });

  // Sort alphabetically by label
  return Array.from(subCatMap.entries())
    .map(([key, { label, count }]) => ({
      key,
      label,
      parentKey: categoryPrefix,
      measurementCount: count
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Extract sub-categories recursively up to `maxDepth` additional levels.
 *
 * For example, with prefix='/metrics' and maxDepth=2:
 *   /metrics/interfaces/counters → Interfaces > Counters
 *   /metrics/system/cpus         → System > CPUs
 *   /metrics/protocols/bgp/neighbors → Protocols > BGP > Neighbors
 *
 * Edge-case: when a measurement path is also a prefix of other measurements
 * (e.g. /metrics/kentik/agent/nms/snmp AND /metrics/kentik/agent/nms/snmp/sources),
 * the parent becomes a group node. To keep the parent measurement selectable, a
 * synthetic leaf child is prepended so the user can still pick it.
 */

function extractSubCategoriesDeepInner(
  measurements: Measurement[],
  measurementPaths: Set<string>,
  displayNameMap: Map<string, string | undefined>,
  categoryPrefix: string,
  maxDepth: number
): SubCategory[] {
  const subs = extractSubCategories(measurements, categoryPrefix);

  return subs.map((sub) => {
    const isRealMeasurement = measurementPaths.has(sub.key);

    // If this sub-category IS a measurement and has no other measurements beneath it, it's a leaf.
    // (measurementCount === 1 means only itself exists at this prefix)
    if (isRealMeasurement && sub.measurementCount === 1) {
      return sub;
    }

    // Always recurse if the sub-category is NOT an actual measurement — we need to
    // drill down until we reach real measurement leaves, regardless of maxDepth.
    // Also recurse when a measurement has deeper measurements beneath it (count > 1)
    // so that those children are represented in the tree.
    // When maxDepth > 0, recurse normally (decrement depth).
    const shouldRecurse = maxDepth > 0 || !isRealMeasurement || sub.measurementCount > 1;

    if (!shouldRecurse) {
      return sub;
    }

    const children = extractSubCategoriesDeepInner(
      measurements,
      measurementPaths,
      displayNameMap,
      sub.key,
      Math.max(maxDepth - 1, 0)
    );

    if (children.length >= 1) {
      // This sub-category has deeper children. If the sub-category's key is ALSO
      // an actual measurement, add a synthetic leaf child so it remains selectable.
      if (isRealMeasurement) {
        const displayName = displayNameMap.get(sub.key);
        const selfChild: SubCategory = {
          key: sub.key,
          label: displayName || sub.label,
          parentKey: sub.key,
          measurementCount: 1,
          isMeasurement: true
        };
        return { ...sub, children: [selfChild, ...children] };
      }

      return { ...sub, children };
    }

    return sub;
  });
}

/**
 * Collapse chains of single-child intermediate group nodes into a single node
 * with a slash-joined label.
 *
 * For example:
 *   Kentik → Agent → NMS → Snmp (with children)
 * becomes:
 *   Kentik/Agent/NMS/Snmp (with children)
 *
 * If a collapsed group ends up with exactly one leaf child, the leaf is absorbed
 * entirely to produce a single selectable node with a slash-joined compound label.
 *
 * Leaf nodes (no children) and nodes with multiple children are never collapsed.
 * Synthetic measurement leaves (isMeasurement) are also never collapsed.
 */
function collapseSubCategory(sub: SubCategory): SubCategory {
  // Leaf node or synthetic measurement leaf — nothing to collapse
  if (!sub.children || sub.children.length === 0 || sub.isMeasurement) {
    return sub;
  }

  // Recursively collapse children first
  let collapsed: SubCategory = { ...sub, children: sub.children.map((child) => collapseSubCategory(child)) };

  // Merge single-child chains: if this intermediate node has exactly one child
  // and that child is also an intermediate node (has children, is not a leaf measurement)
  while (
    collapsed.children &&
    collapsed.children.length === 1 &&
    collapsed.children[0].children &&
    collapsed.children[0].children.length > 0 &&
    !collapsed.children[0].isMeasurement
  ) {
    const child = collapsed.children[0];
    collapsed = {
      ...collapsed,
      key: child.key,
      label: `${collapsed.label}/${child.label}`,
      children: child.children,
      measurementCount: child.measurementCount
    };
  }

  // Final step: if after collapsing chains we have exactly one child that is a leaf
  // (no children of its own), absorb it entirely — the group becomes a single leaf
  // with a compound label. This avoids a group node that only has one selectable item.
  if (
    collapsed.children &&
    collapsed.children.length === 1 &&
    !collapsed.children[0].children &&
    !collapsed.children[0].isMeasurement
  ) {
    const child = collapsed.children[0];
    collapsed = {
      ...collapsed,
      key: child.key,
      label: `${collapsed.label}/${child.label}`,
      children: undefined,
      measurementCount: child.measurementCount
    };
  }

  return collapsed;
}

function collapseSubCategories(subs: SubCategory[]): SubCategory[] {
  return subs.map((sub) => collapseSubCategory(sub));
}

function createCustomMetricsSubCategory(measurements: Measurement[]): SubCategory | null {
  const customMeasurements = measurements.filter(
    (measurement) =>
      measurement.measurement.startsWith('/metrics/') && measurement.tags?.includes(measurementTagEnums.CUSTOM_SNMP)
  );

  if (customMeasurements.length === 0) {
    return null;
  }

  const children = customMeasurements
    .map((measurement) => {
      const lastSegment = measurement.measurement.split('/').filter(Boolean).at(-1) ?? measurement.measurement;
      return {
        key: measurement.measurement,
        label: measurement.displayName || titleizeSegment(lastSegment),
        parentKey: '/metrics/custom',
        measurementCount: 1
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return {
    key: '/metrics/custom',
    label: 'Custom',
    parentKey: '/metrics',
    measurementCount: children.length,
    children
  };
}

export function extractSubCategoriesDeep(
  measurements: Measurement[],
  categoryPrefix: string,
  maxDepth: number = 2
): SubCategory[] {
  const customMetricsSubCategory = categoryPrefix === '/metrics' ? createCustomMetricsSubCategory(measurements) : null;
  const categorizedMeasurements = customMetricsSubCategory
    ? measurements.filter((measurement) => !measurement.tags?.includes(measurementTagEnums.CUSTOM_SNMP))
    : measurements;

  // Build a set of all measurement paths for O(1) lookup
  const measurementPaths = new Set(categorizedMeasurements.map((m) => m.measurement).filter(Boolean));
  // Build a map of measurement path → displayName for O(1) lookup during recursion
  const displayNameMap = new Map<string, string | undefined>(
    categorizedMeasurements.filter((m) => m.measurement).map((m) => [m.measurement, m.displayName])
  );

  const uncollapsedTree = extractSubCategoriesDeepInner(
    categorizedMeasurements,
    measurementPaths,
    displayNameMap,
    categoryPrefix,
    maxDepth
  );
  const collapsedTree = collapseSubCategories(uncollapsedTree);

  if (customMetricsSubCategory) {
    collapsedTree.push(customMetricsSubCategory);
    collapsedTree.sort((a, b) => a.label.localeCompare(b.label));
  }

  return collapsedTree;
}

/** A single selectable option within a breadcrumb dropdown. */
export interface BreadcrumbOption {
  /** Identity of the option — a full measurement/branch path. */
  key: string;
  /** Human-readable label. */
  label: string;
  /** The concrete measurement that will be selected when this option is chosen. */
  measurement: string;
}

/** A single segment (level) of the measurement breadcrumb. */
export interface BreadcrumbSegment {
  /** Label of the currently-selected node at this level. */
  label: string;
  /** Path of the currently-selected node at this level. */
  selectedKey: string;
  /** Sibling options selectable at this level. */
  options: BreadcrumbOption[];
  /** Whether this level should render as an interactive dropdown (more than one option). */
  isDropdown: boolean;
}

/** The full breadcrumb model for the query header. */
export interface MeasurementBreadcrumb {
  /** Static top-level category label (e.g. 'NMS'). */
  categoryLabel: string;
  /** Ordered path segments after the category. */
  segments: BreadcrumbSegment[];
}

/**
 * Build the label for a leaf measurement relative to a branch prefix.
 *
 * Examples (branchKey = '/metrics/interfaces'):
 *   '/metrics/interfaces/counters'                    → 'Counters'
 *   '/metrics/interfaces/subinterfaces/ipv4/addresses'→ 'Subinterfaces / IPv4 / Addresses'
 *   '/metrics/interfaces' (the branch itself)         → displayName || last segment label
 */
function buildLeafLabel(branchKey: string, m: { measurement: string; displayName?: string }): string {
  const relSegments = m.measurement.slice(branchKey.length).split('/').filter(Boolean);
  if (relSegments.length === 0) {
    const branchSegments = branchKey.split('/').filter(Boolean);
    return m.displayName || titleizeSegment(branchSegments[branchSegments.length - 1] ?? branchKey);
  }
  if (relSegments.length === 1) {
    return m.displayName || titleizeSegment(relSegments[0]);
  }
  return relSegments.map(titleizeSegment).join(' / ');
}

/**
 * Build the interactive breadcrumb model for the UDE query header.
 *
 * Given the current category and selected measurement, this produces a static
 * category label plus up to two selectable segments:
 *   1. A top-level branch/leaf picker (siblings within the category, e.g.
 *      Interfaces / System / Devices ...).
 *   2. A leaf-measurement picker listing every concrete measurement under the
 *      selected branch, flattened with compound labels
 *      (e.g. 'Subinterfaces / IPv4 / Addresses').
 *
 * Selecting any option resolves to a concrete measurement (branches resolve to a
 * default leaf), so the caller can reset + apply the new measurement directly.
 *
 * Returns an empty `segments` array (category label only) when there are no
 * sub-categories, for `hideChildren` categories (e.g. Traffic), or when the
 * measurement cannot be located in the category tree.
 */
export function getMeasurementBreadcrumb(params: {
  measurements: Measurement[];
  categoryPrefix: string;
  measurement: string;
  categoryLabel: string;
  hideChildren?: boolean;
}): MeasurementBreadcrumb {
  const { measurements, categoryPrefix, measurement, categoryLabel, hideChildren } = params;

  if (!measurement || hideChildren) {
    return { categoryLabel, segments: [] };
  }

  const topNodes = extractSubCategoriesDeep(measurements, categoryPrefix, 2);
  if (topNodes.length === 0) {
    return { categoryLabel, segments: [] };
  }

  // Locate the top-level branch/leaf that contains the current measurement.
  const selectedTop = topNodes.find((node) => measurement === node.key || measurement.startsWith(`${node.key}/`));
  if (!selectedTop) {
    return { categoryLabel, segments: [] };
  }

  const measurementPaths = new Set(measurements.map((m) => m.measurement).filter(Boolean));

  /** Every concrete measurement under a branch, as sorted breadcrumb options. */
  const leafOptionsUnder = (branchKey: string): BreadcrumbOption[] =>
    measurements
      .filter((m) => m.measurement && (m.measurement === branchKey || m.measurement.startsWith(`${branchKey}/`)))
      .map((m) => ({ key: m.measurement, label: buildLeafLabel(branchKey, m), measurement: m.measurement }))
      .sort((a, b) => a.label.localeCompare(b.label));

  /** Resolve the concrete measurement to apply when a branch/leaf option is picked. */
  const resolveDefaultMeasurement = (node: SubCategory): string => {
    if (measurementPaths.has(node.key)) {
      return node.key;
    }
    const opts = leafOptionsUnder(node.key);
    return opts[0]?.measurement ?? node.key;
  };

  const segments: BreadcrumbSegment[] = [];

  // Segment 0: top-level branch/leaf picker.
  segments.push({
    label: selectedTop.label,
    selectedKey: selectedTop.key,
    options: topNodes.map((node) => ({
      key: node.key,
      label: node.label,
      measurement: resolveDefaultMeasurement(node)
    })),
    isDropdown: topNodes.length > 1
  });

  // Segment 1: leaf-measurement picker — only when the branch has more than one
  // selectable measurement beneath it.
  const leafOptions = leafOptionsUnder(selectedTop.key);
  if (leafOptions.length > 1) {
    const selectedLeaf = leafOptions.find((opt) => opt.key === measurement);
    segments.push({
      label: selectedLeaf?.label ?? buildLeafLabel(selectedTop.key, { measurement }),
      selectedKey: measurement,
      options: leafOptions,
      isDropdown: true
    });
  }

  return { categoryLabel, segments };
}
